'use client'

import { motion } from 'framer-motion'
import { SectionHeader } from '@/components/section-header'
import { ProjectCard } from '@/components/project-card'
import { Container } from '@/components/container'
import { MotionDiv } from '@/components/motion-wrapper'
import { Button } from '@/components/button'
import { projects } from '@/lib/projects-data'
import { trackEvent } from '@/lib/utils'
import { useEffect, useRef } from 'react'

export function ProjectsHome() {
  // Show only first 3 featured projects on home page
  const featuredProjects = projects.slice(0, 3)
  const sectionRef = useRef<HTMLElement>(null)
  const hasTrackedView = useRef(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasTrackedView.current) {
            hasTrackedView.current = true
            trackEvent('section_viewed', {
              event_category: 'engagement',
              event_label: 'projects_preview',
              value: 1,
            })
          }
        })
      },
      { threshold: 0.5 }
    )

    if (sectionRef.current) {
      observer.observe(sectionRef.current)
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current)
      }
    }
  }, [])

  return (
    <section ref={sectionRef} id="projects" className="py-24 bg-white">
      <Container>
        <MotionDiv
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <SectionHeader
            subheading="Case Studies"
            heading="Our Work"
            description="Real projects showcasing how we've helped startups and enterprises transform ideas into scalable products."
          />
        </MotionDiv>

        {/* Gradient Separator */}
        <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent my-12" />

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          viewport={{ once: true }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredProjects.map((project, index) => (
              <ProjectCard key={project.slug} project={project} index={index} />
            ))}
          </div>
        </motion.div>

        <div className="mt-12 text-center">
          <Button 
            href="/projects" 
            variant="outline"
            onClick={() => {
              if (typeof window !== 'undefined' && window.gtag) {
                window.gtag('event', 'cta_clicked', {
                  event_category: 'engagement',
                  event_label: 'view_all_projects',
                  value: 1,
                })
              }
            }}
          >
            View All Case Studies
          </Button>
        </div>
      </Container>
    </section>
  )
}

